import { useEffect, useMemo, useState } from 'react';
import {
  EachWabaTemplatesRes,
  getWorkspaceId,
  useAppSelector,
  getSelectedItemIds,
  useLazyGetTemplateWithIdQuery,
  usePublishMessageMutation,
  getUserData,
  PublishMessagePayload,
  TemplateComponentTypeEnum,
  getCustomerInvite,
} from '@zonofi/zono-money-store';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { MessageType } from '../../constants';
import { onClickBackToWhatsapp } from '../../helper';

export const usePublishTemplate = () => {
  const { templateId = '' } = useParams();

  const workspaceId = useAppSelector(getWorkspaceId);
  const customerIds = useAppSelector(getSelectedItemIds);
  const userData = useAppSelector(getUserData);
  const inviteDetails = useAppSelector(getCustomerInvite);
  const { seller, waConfig } = inviteDetails;

  const [getTemplateWithId, getTemplateWithIdResponse] =
    useLazyGetTemplateWithIdQuery();
  const [publishMessageApi, publishMessageRes] = usePublishMessageMutation();

  const [successState, setSuccessState] = useState(false);
  const [templateData, setTemplateData] = useState<EachWabaTemplatesRes>();
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [activeVar, setActiveVar] = useState<{
    key: string;
    index: string;
  } | null>(null);

  useEffect(() => {
    if (templateId) {
      getTemplateWithId({
        sellerWorkspaceId: workspaceId,
        templateId: templateId,
      });
    }
  }, [templateId]);

  useEffect(() => {
    if (getTemplateWithIdResponse.isSuccess && getTemplateWithIdResponse.data) {
      setTemplateData(getTemplateWithIdResponse.data);
    }
  }, [getTemplateWithIdResponse]);

  const onSendTemplatePress = () => {
    const templateCompData =
      templateData?.templateBody || templateData?.templateDetails;

    const parameters = Object.values(inputValues || {}).map((value) => ({
      text: String(value ?? ''),
      type: 'TEXT',
    }));

    const payload: PublishMessagePayload = {
      sellerWorkspaceId: workspaceId,
      inviteIds: customerIds,
      message: {
        _id: uuidv4(),
        cAt: moment().valueOf().toString(),
        chnType: 'C',
        _v: 2,
        source: 'system',
        sId: userData?.id,
        type: MessageType.WHATSAPP_TEMPLATE,
        user: {
          firstName: userData?.firstName ?? '',
          lastName: userData?.lastName ?? '',
          mobile: userData?.mobile ?? '',
          email: userData?.email ?? '',
          id: userData?.id,
        },
        template: {
          name: templateData?.templateName || templateCompData?.name,
          language: {
            code: templateData?.language || templateCompData?.language,
          },
          components: [
            {
              type: 'BODY',
              parameters,
            },
          ],
        },
      },
    };

    publishMessageApi(payload);
  };

  useEffect(() => {
    if (publishMessageRes.isSuccess) {
      setSuccessState(true);
    }
  }, [publishMessageRes]);

  const isDisabled = useMemo(() => {
    const templateCompData =
      templateData?.templateBody || templateData?.templateDetails;

    const bodyComponent = templateCompData?.components?.find(
      (c: any) => c.type === TemplateComponentTypeEnum.body
    );
    const example = bodyComponent?.example?.body_text?.[0] || [];

    // 👉 no custom → enable
    if (!example.includes('{{custom}}')) return false;

    // 👉 custom exists but empty → disable
    return !(inputValues as any)['custom']?.trim();
  }, [templateData, inputValues]);

  const onWhatsAppPress = () => {
    onClickBackToWhatsapp({
      whatsappPhoneNo: waConfig?.whatsappPhoneNo,
      workspaceId: seller?.workspaceId,
    });
  };

  return {
    onSendTemplatePress,
    inputValues,
    templateData,
    setInputValues,
    isDisabled,
    isLoading: getTemplateWithIdResponse?.isLoading,
    isPublish: publishMessageRes?.isLoading,
    activeVar,
    setActiveVar,
    successState,
    setSuccessState,
    customerIds,
    onWhatsAppPress,
  };
};
